export type BrokerJobStatus =
	| 'PENDING'
	| 'WAITING_DEPS'
	| 'DISPATCHED'
	| 'RUNNING'
	| 'SUCCEEDED'
	| 'FAILED'
	| 'CANCELLED';

export interface BrokerJob {
	jobId: string;
	serviceUUID: string;
	serviceName: string;
	serviceIcon: string;
	serviceVersion: string;
	status: BrokerJobStatus;
	startedAt: number;
	triggeredBy: string;
	triggeredByDisplayName?: string;
	closedAt: number | null;
	active: boolean;
	error?: string;
	output?: object;
}

export const CANCELLABLE_BROKER_JOB_STATUSES: BrokerJobStatus[] = ['PENDING'];

/** The four lifecycle states the Broker search index exposes (PENDING/DISPATCHED/WAITING_DEPS are folded into RUNNING). */
export type TenantJobSearchStatus = 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED';

/** How the Broker search index folds the detailed lifecycle states into those four. */
export const TENANT_JOB_SEARCH_STATUS_OF: Record<BrokerJobStatus, TenantJobSearchStatus> = {
	PENDING: 'RUNNING',
	WAITING_DEPS: 'RUNNING',
	DISPATCHED: 'RUNNING',
	RUNNING: 'RUNNING',
	SUCCEEDED: 'SUCCEEDED',
	FAILED: 'FAILED',
	CANCELLED: 'CANCELLED',
};

/** Every search status, in lifecycle order. */
export const TENANT_JOB_SEARCH_STATUSES: TenantJobSearchStatus[] = ['RUNNING', 'SUCCEEDED', 'FAILED', 'CANCELLED'];

export interface BrokerService {
	serviceUUID: string;
	serviceName: string;
	active: boolean;
	inputType?: string;
	requiredPermissions?: string[];
	serviceIcon?: string;
	regexFilter?: Record<string, string | undefined>;
	/**
	 * The Broker's content-key paths for the service (`services.json` `key`), e.g. `references[*].nxObject.eTag`
	 * or `references[*].nxObject.uuid`, optionally followed by `params.*` paths. They decide what a job id names.
	 */
	key?: string[];
}

export interface NxPurgeJobOutput {
	status?: string;
	s3Bucket?: string;
	s3Key?: string;
	recoverable?: boolean;
	alreadyAbsent?: boolean;
}

const NX_PURGE_JOB_OUTPUT_FIELD_TYPES: Record<keyof NxPurgeJobOutput, 'string' | 'boolean'> = {
	status: 'string',
	s3Bucket: 'string',
	s3Key: 'string',
	recoverable: 'boolean',
	alreadyAbsent: 'boolean',
};

// The broker spec leaves `output` untyped: each service writes its own shape, so narrow it at the point of use.
export function isNxPurgeJobOutput(output: unknown): output is NxPurgeJobOutput {
	if (typeof output !== 'object' || output === null || Array.isArray(output)) return false;

	const record = output as Record<string, unknown>;
	return Object.entries(NX_PURGE_JOB_OUTPUT_FIELD_TYPES).every(
		([field, type]) => record[field] === undefined || typeof record[field] === type,
	);
}

export interface BrokerJobDetails {
	jobId: string;
	serviceUUID: string;
	serviceName: string;
	serviceVersion: string;
	status: BrokerJobStatus;
	priority: number;
	deps: string[];
	cacheHits: string[];
	createdAt: number;
	triggeredBy: string;
	triggeredByDisplayName?: string;
	dispatchedAt?: number;
	finishedAt?: number;
	workStarted?: boolean;
	error?: string;
	output?: unknown;
}
